import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Calendar,
  CalendarDays,
  ChevronLeft,
  Clock,
  CheckCircle2,
  Loader2,
  FileText,
  Building2,
  Star,
  ExternalLink,
  RefreshCw,
  Save,
  ArrowLeft,
  AlertCircle,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper pour obtenir le favicon
const getFaviconUrl = (url) => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch { return null; }
};

// Composant pour afficher une carte de site en mode lecture seule
const StaticSiteCard = ({ site }) => {
  const todayPubs = site.publications || [];
  const weekendPubs = site.weekend_publications || [];
  const stats = site.stats || {};
  
  return (
    <Card className="overflow-hidden border border-slate-700/50 bg-slate-900/90">
      {/* Header */}
      <div className="bg-slate-800 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {site.url && (
              <img 
                src={getFaviconUrl(site.url)} 
                alt="" 
                className="w-5 h-5 rounded bg-white p-0.5"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <h3 className="font-bold text-sm uppercase tracking-wide text-white">{site.name}</h3>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300">{stats.published || 0} publiés</span>
            <span className="px-2 py-1 rounded bg-amber-500/20 text-amber-300">{stats.draft || 0} brouillons</span>
            {(stats.forgotten || 0) > 0 && (
              <span className="px-2 py-1 rounded bg-red-500/20 text-red-300">{stats.forgotten} oubliés</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <CardContent className="p-3 space-y-3">
        {/* Objectif */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400">Objectif : {site.daily_objective || 2} / jour {site.strict_objective && "(strict)"}</span>
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            site.site_category === 'groupe' ? 'bg-sky-500/20 text-sky-300' :
            site.site_category === 'va' ? 'bg-slate-500/20 text-slate-300' :
            'bg-slate-600/20 text-slate-400'
          }`}>
            {site.site_category?.toUpperCase() || 'AUTRE'}
          </span>
        </div>
        
        {/* Publications du jour */}
        {todayPubs.length > 0 ? (
          <div className="max-h-[180px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900/95">
                <tr className="text-slate-500 uppercase">
                  <th className="text-left py-1.5 font-medium">Article</th>
                  <th className="text-left py-1.5 font-medium w-20">Auteur</th>
                  <th className="text-center py-1.5 font-medium w-12">Statut</th>
                </tr>
              </thead>
              <tbody>
                {todayPubs.map((pub, idx) => (
                  <tr key={idx} className="border-t border-slate-700/30">
                    <td className="py-1.5">
                      <span className="text-slate-200 line-clamp-1" title={pub.title}>
                        {pub.title || 'Sans titre'}
                      </span>
                    </td>
                    <td className="py-1.5 text-slate-400 truncate">
                      {(pub.author_name || pub.user_name || '').split(' ')[0]}
                    </td>
                    <td className="py-1.5 text-center">
                      <span 
                        className={`inline-block w-2 h-2 rounded-full ${
                          pub.publish_status === 'publish' ? 'bg-emerald-500' :
                          pub.publish_status === 'forgotten' ? 'bg-red-500' :
                          'bg-amber-500'
                        }`}
                        title={
                          pub.publish_status === 'publish' ? 'Publié' :
                          pub.publish_status === 'forgotten' ? 'Oublié' :
                          'Brouillon'
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500 text-xs">
            Aucune publication ce jour
          </div>
        )}
        
        {/* Weekend publications */}
        {weekendPubs.length > 0 && (
          <div className="border-t border-slate-600/50 pt-2 mt-2">
            <div className="text-xs text-sky-400 font-medium mb-1">Week-end ({weekendPubs.length})</div>
            <div className="text-xs text-slate-400">
              {weekendPubs.map(p => p.title).join(', ').substring(0, 100)}...
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Page principale
const ConduiteHistoryPage = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const isAmiral = user?.grade === 'amiral';
  
  // Charger la liste des historiques
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/conduite/history?limit=60`);
      setHistory(res.data?.history || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Charger le détail d'une date
  const loadSnapshot = useCallback(async (date) => {
    try {
      setLoadingSnapshot(true);
      setSelectedDate(date);
      const res = await axios.get(`${API}/api/conduite/history/${date}`);
      setSnapshot(res.data);
    } catch (error) {
      toast.error(`Erreur lors du chargement du ${date}`);
      setSnapshot(null);
    } finally {
      setLoadingSnapshot(false);
    }
  }, []);
  
  // Sauvegarder manuellement (remplace si existant)
  const handleManualSave = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`${API}/api/conduite/history/save`);
      toast.success(res.data?.message || 'Snapshot sauvegardé');
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  // Supprimer un snapshot (amiraux uniquement)
  const handleDeleteSnapshot = async (date) => {
    try {
      await axios.delete(`${API}/api/conduite/history/${date}`);
      toast.success(`Snapshot du ${formatDate(date)} supprimé`);
      loadHistory();
      // Si on était sur le détail de ce snapshot, retourner à la liste
      if (selectedDate === date) {
        setSelectedDate(null);
        setSnapshot(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  
  // Formater la date pour l'affichage
  const formatDate = (dateStr) => {
    // Check if it's a weekend snapshot (format: YYYY-MM-DD_weekend)
    if (dateStr.includes('_weekend')) {
      const saturdayDate = dateStr.replace('_weekend', '');
      const saturday = new Date(saturdayDate);
      const sunday = new Date(saturday);
      sunday.setDate(sunday.getDate() + 1);
      
      const satFormatted = saturday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
      const sunFormatted = sunday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      
      return `Week-end du ${satFormatted} au ${sunFormatted}`;
    }
    
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Check if a snapshot is a weekend snapshot
  const isWeekendSnapshot = (item) => {
    return item.is_weekend || item.date?.includes('_weekend');
  };
  
  // Vue détail d'un snapshot
  if (selectedDate && snapshot) {
    const groupeSites = snapshot.sites?.filter(s => s.site_category === 'groupe') || [];
    const vaSites = snapshot.sites?.filter(s => s.site_category === 'va') || [];
    const autreSites = snapshot.sites?.filter(s => s.site_category === 'autre' || !s.site_category) || [];
    const isWeekend = isWeekendSnapshot(snapshot);
    
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="conduite-history-detail">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                onClick={() => { setSelectedDate(null); setSnapshot(null); }}
                variant="ghost" 
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Retour
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-white">
                    Conduite du {formatDate(snapshot.date)}
                  </h1>
                  {isWeekend && (
                    <span className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold uppercase border border-violet-500/30">
                      <CalendarDays className="w-3 h-3 inline mr-1" />
                      Week-end
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">
                  Sauvegardé le {new Date(snapshot.created_at).toLocaleString('fr-FR')} 
                  {snapshot.triggered_by && ` (${snapshot.triggered_by})`}
                </p>
              </div>
            </div>
            
            {/* Stats globales + Bouton supprimer */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-lg font-bold text-emerald-400">{snapshot.stats?.published || 0}</span>
                <span className="text-xs text-emerald-400/80">publiés</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <Loader2 className="w-4 h-4 text-amber-400" />
                <span className="text-lg font-bold text-amber-400">{snapshot.stats?.draft || 0}</span>
                <span className="text-xs text-amber-400/80">brouillons</span>
              </div>
              {(snapshot.stats?.forgotten || 0) > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-lg font-bold text-red-400">{snapshot.stats.forgotten}</span>
                  <span className="text-xs text-red-400/80">oubliés</span>
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-600/10 border border-slate-600/30">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-lg font-bold text-slate-400">{snapshot.stats?.total_publications || 0}</span>
                <span className="text-xs text-slate-400/80">total</span>
              </div>
              
              {/* Bouton supprimer (amiraux uniquement) */}
              {isAmiral && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Supprimer ce snapshot ?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        Vous êtes sur le point de supprimer le snapshot du <span className="font-semibold text-sky-400">{formatDate(snapshot.date)}</span>.
                        Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDeleteSnapshot(snapshot.date)}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>
        
        {/* Contenu */}
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Section Groupe */}
          {groupeSites.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-4 bg-slate-800/50 border border-slate-600/30 rounded-lg px-4 py-2">
                <Building2 className="w-5 h-5 text-sky-400" />
                <h2 className="text-xl font-bold text-white uppercase tracking-wide">Groupe</h2>
                <span className="text-xs text-slate-300 ml-auto">{groupeSites.length} sites</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {groupeSites.map(site => (
                  <StaticSiteCard key={site.id} site={site} />
                ))}
              </div>
            </section>
          )}
          
          {/* Section VA */}
          {vaSites.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-4 bg-slate-800/40 border border-slate-600/30 rounded-lg px-4 py-2">
                <Star className="w-5 h-5 text-slate-300" />
                <h2 className="text-xl font-bold text-white uppercase tracking-wide">VA</h2>
                <span className="text-xs text-slate-300 ml-auto">{vaSites.length} sites</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {vaSites.map(site => (
                  <StaticSiteCard key={site.id} site={site} />
                ))}
              </div>
            </section>
          )}
          
          {/* Section Autres */}
          {autreSites.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-4 bg-slate-800/30 border border-slate-700/30 rounded-lg px-4 py-2">
                <FileText className="w-5 h-5 text-slate-400" />
                <h2 className="text-xl font-bold text-white uppercase tracking-wide">Autres</h2>
                <span className="text-xs text-slate-300 ml-auto">{autreSites.length} sites</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {autreSites.map(site => (
                  <StaticSiteCard key={site.id} site={site} />
                ))}
              </div>
            </section>
          )}
          
          {/* Tribunes */}
          {snapshot.tribunes && snapshot.tribunes.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-4 bg-slate-800/30 border border-slate-700/30 rounded-lg px-4 py-2">
                <FileText className="w-5 h-5 text-slate-400" />
                <h2 className="text-xl font-bold text-white uppercase tracking-wide">Tribunes</h2>
                <span className="text-xs text-slate-300 ml-auto">{snapshot.tribunes.length}</span>
              </div>
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardContent className="p-4">
                  <div className="space-y-2">
                    {snapshot.tribunes.map((tribune, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                        <span className="text-sm text-slate-200">{tribune.title || tribune.subject}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          tribune.status === 'published' ? 'bg-sky-500/20 text-sky-300' : 'bg-slate-600/50 text-slate-400'
                        }`}>
                          {tribune.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
        </div>
      </div>
    );
  }
  
  // Vue liste des historiques
  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="conduite-history-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calendar className="w-8 h-8 text-sky-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Historique de la Conduite</h1>
              <p className="text-sm text-slate-400">Snapshots sauvegardés chaque jour à minuit</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadHistory} disabled={loading} variant="outline" size="sm" className="border-slate-600 text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {isAmiral && (
              <Button onClick={handleManualSave} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Sauvegarder maintenant
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Liste des jours */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          </div>
        ) : history.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-700/50">
            <CardContent className="py-20 text-center">
              <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-300 mb-2">Aucun historique disponible</h3>
              <p className="text-slate-500 mb-6">
                Les snapshots sont sauvegardés automatiquement chaque jour à minuit.
              </p>
              {isAmiral && (
                <Button onClick={handleManualSave} disabled={saving} className="bg-sky-600 hover:bg-sky-500">
                  <Save className="w-4 h-4 mr-2" /> Créer le premier snapshot
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {history.map((item) => {
              const isWeekend = isWeekendSnapshot(item);
              return (
                <Card 
                  key={item.date}
                  className={`border hover:border-sky-500/30 transition-all cursor-pointer ${
                    isWeekend 
                      ? 'bg-violet-950/30 border-violet-700/50 hover:bg-violet-900/30' 
                      : 'bg-slate-900/50 border-slate-700/50 hover:bg-slate-800/50'
                  }`}
                  onClick={() => loadSnapshot(item.date)}
                >
                  <CardContent className="py-4 px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isWeekend ? 'bg-violet-800/50' : 'bg-slate-800'
                        }`}>
                          {isWeekend ? (
                            <CalendarDays className="w-6 h-6 text-violet-400" />
                          ) : (
                            <Calendar className="w-6 h-6 text-sky-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{formatDate(item.date)}</h3>
                            {isWeekend && (
                              <span className="px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 text-[10px] font-bold uppercase">
                                Week-end
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            Sauvegardé à {new Date(item.created_at).toLocaleTimeString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {item.stats && (
                          <>
                            <div className="text-center">
                              <div className="text-lg font-bold text-emerald-400">{item.stats.published || 0}</div>
                              <div className="text-[10px] text-slate-500 uppercase">Publiés</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-amber-400">{item.stats.draft || 0}</div>
                              <div className="text-[10px] text-slate-500 uppercase">Brouillons</div>
                            </div>
                            {(item.stats.forgotten || 0) > 0 && (
                              <div className="text-center">
                                <div className="text-lg font-bold text-red-400">{item.stats.forgotten}</div>
                                <div className="text-[10px] text-slate-500 uppercase">Oubliés</div>
                              </div>
                            )}
                            <div className="text-center">
                              <div className="text-lg font-bold text-slate-500">{item.stats.total_sites || 0}</div>
                              <div className="text-[10px] text-slate-500 uppercase">Sites</div>
                            </div>
                          </>
                        )}
                        
                        {/* Bouton supprimer (amiraux uniquement) */}
                        {isAmiral && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                title="Supprimer ce snapshot"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-900 border-slate-700" onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Supprimer ce snapshot ?</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">
                                  Vous êtes sur le point de supprimer le snapshot du <span className="font-semibold text-sky-400">{formatDate(item.date)}</span>.
                                  Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">Annuler</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSnapshot(item.date); }}
                                  className="bg-red-600 text-white hover:bg-red-700"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        
                        <ChevronLeft className="w-5 h-5 text-slate-500 rotate-180" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConduiteHistoryPage;
